import { supabase } from '@/lib/supabase';
import { 
  Ticket, 
  TicketComment, 
  TicketAttachment, 
  TicketHistory,
  CreateTicketInput,
  UpdateTicketInput,
  CreateCommentInput,
  CreateAttachmentInput,
  TicketCategory
} from '@/types/ticket';
import { ticketAssignmentService } from './ticketAssignmentService';
import { ticketNotificationService } from './ticketNotificationService';
import { webhookService } from './webhookService';
import { getCurrentTenantId } from '@/utils/tenantUtils';

// Type definitions removed as they're not being used

// Helper function to get category acronym
const getCategoryAcronym = (category: TicketCategory): string => {
  switch (category) {
    case TicketCategory.SWA_REPORTING_BUG:
    case TicketCategory.SWA_FEATURE_REQUEST:
      return 'SWA';
    case TicketCategory.SST_ISSUE:
      return 'SST';
    case TicketCategory.THR_ISSUE:
      return 'THR';
    case TicketCategory.D2D_ISSUE:
      return 'D2D';
    case TicketCategory.GENERAL_TECH_ISSUE:
      return 'GTI';
    case TicketCategory.GENERAL_SUPPORT_ISSUE:
      return 'GSI';
    case TicketCategory.TEAM_ASSIGNMENT:
      return 'TMA';
    case TicketCategory.MISSING_PROJECT_DATA:
      return 'MPD';
    default:
      return 'UNK';
  }
};

// Helper function to get user details
const getUserDetails = async (userId: string) => {
  if (!userId) return null;
  const { data: profile } = await supabase
    .from('user_profiles')
    .select('id, name, email')
    .eq('id', userId)
    .single();
  return profile;
};

// Helper function to get biz dev rep details
export const getBizDevRepDetails = async (userId: string) => {
  if (!userId) return null;
  
  // First get the user's team
  const { data: userTeam } = await supabase
    .from('user_teams')
    .select(`
      team_id,
      teams (
        id,
        name,
        biz_dev_id
      )
    `)
    .eq('user_id', userId)
    .single() as { data: { team_id: string; teams: { id: string; name: string; biz_dev_id: string | null } } | null };
  
  if (!userTeam?.team_id || !userTeam.teams?.name) return null;
  
  // If the team has a biz_dev_id, use that
  if (userTeam.teams.biz_dev_id) {
    const bizDevUser = await getUserDetails(userTeam.teams.biz_dev_id);
    return {
      user: bizDevUser,
      team: userTeam.teams.name
    };
  }
  
  // Otherwise, look for a Biz Dev Rep in the team
  const { data: teamMember } = await supabase
    .from('project_teams')
    .select('user_id')
    .eq('team_id', userTeam.team_id)
    .eq('role', 'Biz Dev Rep')
    .single();
  
  if (!teamMember?.user_id) return null;
  
  return {
    user: await getUserDetails(teamMember.user_id),
    team: userTeam.teams.name
  };
};

export const ticketService = {
  // Tickets
  async getTickets(tenantId?: string): Promise<Ticket[]> {
    try {
      // Get the current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Get tenant ID if not provided
      let currentTenantId = tenantId;
      if (!currentTenantId) {
        const { data: fetchedTenantId, error: tenantError } = await supabase.rpc('get_current_user_tenant_id');
        if (tenantError) {
          console.error('Error getting tenant ID:', tenantError);
          throw new Error('Failed to get tenant ID');
        }
        currentTenantId = fetchedTenantId;
      }

      if (!currentTenantId) {
        throw new Error('No tenant ID available');
      }

      // First fetch tickets with basic info, filtered by tenant
      const { data: tickets, error: ticketsError } = await supabase
        .from('tickets')
        .select(`
          *,
          user_profiles!created_by (
            id,
            name,
            email
          ),
          assigned_user:user_profiles!assigned_to (
            id,
            name,
            email
          )
        `)
        .eq('tenant_id', currentTenantId)
        .order('created_at', { ascending: false });

      if (ticketsError) {
        console.error('Error fetching tickets:', ticketsError);
        throw ticketsError;
      }

      // Then fetch comments and read status for each ticket
      const processedTickets = await Promise.all((tickets || []).map(async (ticket) => {
        const { data: comments } = await supabase
          .from('ticket_comments')
          .select(`
            id,
            created_by,
            ticket_comment_read_status (
              is_read,
              user_id
            )
          `)
          .eq('ticket_id', ticket.id);

        let unreadCount = 0;
        if (ticket.assigned_to === user.id) {
          unreadCount = (comments || []).filter(comment => {
            // Only consider comments not made by the current user
            if (comment.created_by === user.id) return false;
            // Find a read status for the current user
            const readStatus = comment.ticket_comment_read_status?.find(
              (status: { user_id: string; is_read: boolean }) => 
                status.user_id === user.id
            );
            // Unread if no readStatus for user, or if is_read is false
            return !readStatus || readStatus.is_read === false;
          }).length;
        }

        return {
          ...ticket,
          unread_comments_count: unreadCount
        };
      }));

      return processedTickets;
    } catch (error) {
      console.error('Error fetching tickets:', error);
      throw error;
    }
  },

  async getTicketById(id: string, tenantId?: string): Promise<Ticket> {
    try {
      // Get tenant ID if not provided
      let currentTenantId = tenantId;
      if (!currentTenantId) {
        const { data: fetchedTenantId, error: tenantError } = await supabase.rpc('get_current_user_tenant_id');
        if (tenantError) {
          console.error('Error getting tenant ID:', tenantError);
          throw new Error('Failed to get tenant ID');
        }
        currentTenantId = fetchedTenantId;
      }

      if (!currentTenantId) {
        throw new Error('No tenant ID available');
      }

      const { data: ticket, error } = await supabase
        .from('tickets')
        .select(`
          *,
          user_profiles!created_by (
            id,
            name,
            email
          ),
          assigned_user:user_profiles!assigned_to (
            id,
            name,
            email
          )
        `)
        .eq('id', id)
        .eq('tenant_id', currentTenantId)
        .single();

      if (error) {
        console.error('Error fetching ticket:', error);
        throw error;
      }
      
      if (!ticket) {
        console.error('No ticket found with ID:', id);
        throw new Error('Ticket not found');
      }      
      return ticket;
    } catch (error) {
      console.error('Error in getTicketById:', error);
      throw error;
    }
  },

  async createTicket(input: Omit<CreateTicketInput, 'title'>): Promise<Ticket> {
    try {
      // Get the current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Get user's first name and team
      const { data: userProfile, error: userError } = await supabase
        .from('users')
        .select(`
          first_name,
          user_teams!user_id (
            team_id,
            teams!team_id (
              name
            )
          )
        `)
        .eq('id', user.id)
        .single();

      if (userError) {
        console.error('Error fetching user profile:', userError);
        throw userError;
      }

      // Safely access team name with proper type checking
      const userTeams = (userProfile?.user_teams as unknown) as Array<{ 
        team_id: string; 
        teams: { name: string } 
      }> | undefined;
      
      // Now safely access the team name
      const teamName = userTeams?.[0]?.teams?.name || 'SOL';
      const firstName = userProfile?.first_name || 'User';

      // Get the highest ticket number from existing tickets
      const { data: tickets, error: ticketsError } = await supabase
        .from('tickets')
        .select('title')
        .order('created_at', { ascending: false });

      if (ticketsError) {
        console.error('Error fetching tickets:', ticketsError);
        throw ticketsError;
      }

      // Extract numbers from titles and find the highest
      const numbers = (tickets || [])
        .map(ticket => {
          if (!ticket.title) return 0;
          const parts = ticket.title.split(' - ');
          if (parts.length === 0) return 0;
          const number = parseInt(parts[0]);
          return isNaN(number) ? 0 : number;
        })
        .filter(num => num > 0);

      const highestNumber = numbers.length > 0 ? Math.max(...numbers) : 0;
      const nextTicketNumber = String(highestNumber + 1).padStart(3, '0');

      // Generate the title
      const title = `${nextTicketNumber} - ${getCategoryAcronym(input.category)} - ${input.priority} - ${teamName} - ${firstName}`;

      // Get the assigned user based on category
      const assignedUser = await ticketAssignmentService.getAssignedUser(input.category);

      // Create the ticket with the generated title and assigned user (if any)
      const tenantId = await getCurrentTenantId();
      
      const { data: ticket, error: createError } = await supabase
        .from('tickets')
        .insert({
          title,
          description: input.description,
          priority: input.priority,
          category: input.category,
          loom_link: input.loom_link,
          created_by: user.id,
          project_id: input.project_id,
          due_date: input.due_date,
          assigned_to: assignedUser || null,
          tenant_id: tenantId
        })
        .select()
        .single();

      if (createError) {
        console.error('Error creating ticket in database:', createError);
        throw createError;
      }

      // Get user details for webhook payload
      const [creatorDetails, assignedUserDetails, bizDevRepDetails] = await Promise.all([
        getUserDetails(user.id),
        assignedUser ? getUserDetails(assignedUser) : null,
        getBizDevRepDetails(user.id)
      ]);

      // Prepare all async operations to run in parallel
      const asyncOperations = [];

      // Prepare notification for ticket creator
      asyncOperations.push(
        ticketNotificationService.createNotification({
          ticket_id: ticket.id,
          user_id: user.id,
          type: 'ticket_created',
          message: `Your ticket has been created and assigned to ${assignedUserDetails?.name || 'Unknown'}.`
        }).catch(error => {
          console.error('Error creating notification for ticket creator:', error);
          return null;
        })
      );

      // Prepare notification for assigned user
      if (assignedUser) {
        asyncOperations.push(
          ticketNotificationService.createNotification({
            ticket_id: ticket.id,
            user_id: assignedUser,
            type: 'ticket_created',
            message: `A new ticket has been assigned to you: ${title}`
          }).catch(error => {
            console.error('Error creating notification for assigned user:', error);
            return null;
          })
        );
      }

      // Prepare webhook for ticket creation
      asyncOperations.push(
        webhookService.triggerWebhooks('ticket_created', {
          ticket_id: ticket.id,
          title: ticket.title,
          description: ticket.description,
          priority: ticket.priority,
          category: ticket.category,
          status: ticket.status,
          created_by: {
            name: creatorDetails?.name || 'Unknown',
            email: creatorDetails?.email || 'Unknown',
            team: bizDevRepDetails?.team || 'Unknown'
          },
          assigned_to: assignedUserDetails ? {
            name: assignedUserDetails.name || 'Unknown',
            email: assignedUserDetails.email || 'Unknown'
          } : null,
          biz_dev_rep: bizDevRepDetails?.user ? {
            name: bizDevRepDetails.user.name || 'Unknown',
            email: bizDevRepDetails.user.email || 'Unknown'
          } : null,
          project_id: ticket.project_id,
          due_date: ticket.due_date,
          created_at: ticket.created_at
        }).catch(error => {
          console.error('Error triggering ticket creation webhook:', error);
          return null;
        })
      );

      // Run all operations in parallel
      await Promise.all(asyncOperations);

      return ticket;
    } catch (error) {
      console.error('Error creating ticket:', error);
      throw error;
    }
  },

  async updateTicket(id: string, input: UpdateTicketInput): Promise<Ticket> {
    try {
      // Get the current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Check if user is admin by checking email domain
      const isAdmin = user.email?.endsWith('@solerro.com') || false;

      // First get the current ticket to verify it exists and check permissions
      const { data: currentTicket, error: getError } = await supabase
        .from('tickets')
        .select('*')
        .eq('id', id)
        .single();

      if (getError) {
        console.error('Error getting current ticket:', getError);
        throw getError;
      }

      if (!currentTicket) {
        throw new Error('Ticket not found');
      }

      // Check if user has permission to update this ticket
      const canUpdate = isAdmin || 
        user.id === currentTicket.created_by || 
        user.id === currentTicket.assigned_to;

      if (!canUpdate) {
        throw new Error('You do not have permission to update this ticket');
      }

      // Create update data
      const updateData: any = {};

      // Only include fields that are actually changing
      if (input.status !== undefined) {
        updateData.status = input.status;
      }
      if (input.priority !== undefined) {
        updateData.priority = input.priority;
      }
      if (input.assigned_to !== undefined) {
        updateData.assigned_to = input.assigned_to;
      }
      if (input.due_date !== undefined) {
        updateData.due_date = input.due_date;
      }
      if (input.loom_link !== undefined) {
        updateData.loom_link = input.loom_link;
      }

      // Only include internal_priority if user is admin and it's changing
      if (isAdmin && input.internal_priority !== undefined) {
        updateData.internal_priority = input.internal_priority;
      }

      // If no fields to update, return current ticket
      if (Object.keys(updateData).length === 0) {
        return currentTicket;
      }

      // Perform the update
      const { data: updatedTicket, error: updateError } = await supabase
        .from('tickets')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (updateError) {
        console.error('Error updating ticket:', updateError);
        throw updateError;
      }

      if (!updatedTicket) {
        throw new Error('Failed to update ticket - no rows returned');
      }

      // Send notification for status change
      if (input.status && input.status !== currentTicket.status) {
        const statusMessage = `Ticket status changed from ${currentTicket.status} to ${input.status}`;
        
        // Create a single notification for the status change
        const notificationData = {
          ticket_id: id,
          type: 'status_change',
          message: statusMessage,
          created_by: user.id
        };

        // Send notification to ticket creator
        if (currentTicket.created_by) {
          await ticketNotificationService.createNotification({
            ...notificationData,
            user_id: currentTicket.created_by
          });
        }

        // Send notification to assigned user
        if (currentTicket.assigned_to && currentTicket.assigned_to !== currentTicket.created_by) {
          await ticketNotificationService.createNotification({
            ...notificationData,
            user_id: currentTicket.assigned_to
          });
        }

        // Comment out biz dev rep notification for now
        /*
        if (bizDevRepDetails?.user?.id) {
          await ticketNotificationService.createNotification({
            ...notificationData,
            user_id: bizDevRepDetails.user.id
          });
        }
        */

        // Get user details for webhook payload
        const [creatorDetails, assignedUserDetails, bizDevRepDetails] = await Promise.all([
          getUserDetails(currentTicket.created_by),
          currentTicket.assigned_to ? getUserDetails(currentTicket.assigned_to) : null,
          getBizDevRepDetails(currentTicket.created_by)
        ]);

        // Trigger webhook for status change
        await webhookService.triggerWebhooks('ticket_status_changed', {
          ticket_id: updatedTicket.id,
          title: updatedTicket.title,
          description: updatedTicket.description,
          priority: updatedTicket.priority,
          status: updatedTicket.status,
          created_by: {
            name: creatorDetails?.name || 'Unknown',
            email: creatorDetails?.email || 'Unknown',
            team: bizDevRepDetails?.team || 'Unknown'
          },
          assigned_to: assignedUserDetails ? {
            name: assignedUserDetails.name || 'Unknown',
            email: assignedUserDetails.email || 'Unknown'
          } : null,
          biz_dev_rep: bizDevRepDetails?.user ? {
            name: bizDevRepDetails.user.name || 'Unknown',
            email: bizDevRepDetails.user.email || 'Unknown'
          } : null,
          old_status: currentTicket.status,
          new_status: updatedTicket.status,
          changed_at: updatedTicket.updated_at
        });
      }

      // Send notification for assignment change
      if (input.assigned_to !== undefined && input.assigned_to !== currentTicket.assigned_to) {
        const assignmentMessage = input.assigned_to 
          ? `You have been assigned to ticket: ${currentTicket.title}`
          : `You have been unassigned from ticket: ${currentTicket.title}`;
        
        // Create a single notification for the assignment change
        const notificationData = {
          ticket_id: id,
          type: 'assignment_change',
          message: assignmentMessage,
          created_by: user.id
        };

        // Notify the new assigned user
        if (input.assigned_to) {
          await ticketNotificationService.createNotification({
            ...notificationData,
            user_id: input.assigned_to
          });
        }

        // Notify the previous assigned user if they're being unassigned
        if (currentTicket.assigned_to && !input.assigned_to) {
          await ticketNotificationService.createNotification({
            ...notificationData,
            user_id: currentTicket.assigned_to
          });
        }
      }

      return updatedTicket;
    } catch (error) {
      console.error('Error updating ticket:', error);
      throw error;
    }
  },

  // Comments
  async getTicketComments(ticketId: string): Promise<TicketComment[]> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data: comments, error } = await supabase
        .from('ticket_comments')
        .select(`
          *,
          user_profiles!created_by (
            id,
            name,
            email
          ),
          ticket_comment_read_status!comment_id (
            is_read,
            read_at,
            user_id
          )
        `)
        .eq('ticket_id', ticketId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      // Process comments to include read status for current user
      const processedComments = (comments || []).map(comment => {
        
        const readStatus = comment.ticket_comment_read_status?.find(
          (status: { user_id: string; is_read: boolean; read_at: string | null }) => {
            return status.user_id === user.id;
          }
        );
        
        
        return {
          ...comment,
          read_status: readStatus ? {
            is_read: readStatus.is_read,
            read_at: readStatus.read_at
          } : undefined
        };
      });

      return processedComments;
    } catch (error) {
      console.error('Error fetching comments:', error);
      throw error;
    }
  },

  async createComment(input: CreateCommentInput): Promise<TicketComment> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Log the input payload

      // Get the ticket and user details in parallel
      const [ticketResult, userProfileResult] = await Promise.all([
        supabase
          .from('tickets')
          .select('*')
          .eq('id', input.ticket_id)
          .single(),
        supabase
          .from('user_profiles')
          .select('id, name, email')
          .eq('id', user.id)
          .single()
      ]);

      if (ticketResult.error) throw ticketResult.error;
      if (!ticketResult.data) throw new Error('Ticket not found');

      const ticket = ticketResult.data;
      const userProfile = userProfileResult.data;

      // Create the comment
      const tenantId = await getCurrentTenantId();
      
      const { data: comment, error } = await supabase
        .from('ticket_comments')
        .insert({
          ticket_id: input.ticket_id,
          content: input.content,
          is_internal: input.is_internal || false,
          created_by: user.id,
          mentioned_user_ids: input.mentioned_user_ids || [],
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          tenant_id: tenantId
        })
        .select()
        .single();

      if (error) {
        console.error('[ticketService.createComment] Error:', error);
        if (error.message) console.error('[ticketService.createComment] Error message:', error.message);
        if (error.details) console.error('[ticketService.createComment] Error details:', error.details);
        throw error;
      }

      // Add user profile to the comment
      if (userProfile) {
        comment.user_profiles = {
          id: userProfile.id,
          name: userProfile.name,
          email: userProfile.email
        };
      }

      // Create read status entries for mentioned users
      if (input.mentioned_user_ids && input.mentioned_user_ids.length > 0) {
        const readStatusEntries = input.mentioned_user_ids.map(userId => ({
          comment_id: comment.id,
          user_id: userId,
          is_read: false,
          tenant_id: tenantId
        }));

        const { error: readStatusError } = await supabase
          .from('ticket_comment_read_status')
          .insert(readStatusEntries);

        if (readStatusError) {
          console.error('Error creating read status entries:', readStatusError);
        }
      }

      // Run notifications and webhooks in parallel
      const notificationPromises = [];
      const webhookData = {
        ticket_id: ticket.id,
        title: ticket.title,
        description: ticket.description,
        priority: ticket.priority,
        status: ticket.status,
        created_by: {
          name: userProfile?.name || 'Unknown',
          email: userProfile?.email || 'Unknown'
        },
        comment: {
          content: input.content,
          is_internal: input.is_internal,
          created_by: {
            name: userProfile?.name || 'Unknown',
            email: userProfile?.email || 'Unknown'
          },
          created_at: comment.created_at,
          mentioned_user_ids: input.mentioned_user_ids
        }
      };

      console.log('[ticketService] Starting notification process:', {
        ticketId: ticket.id,
        mentionedUserIds: input.mentioned_user_ids,
        commentId: comment.id,
        isInternal: input.is_internal
      });

      // Send notifications for mentions
      if (input.mentioned_user_ids && input.mentioned_user_ids.length > 0) {
        console.log('[ticketService] Processing mentions for users:', input.mentioned_user_ids);

        for (const mentionedUserId of input.mentioned_user_ids) {
          if (mentionedUserId !== user.id) { // Don't notify self
            console.log('[ticketService] Checking notification preferences for user:', mentionedUserId);
            
            // Check user's notification preferences first
            const { data: preferences, error: prefError } = await supabase
              .rpc('get_notification_preferences', {
                p_user_id: mentionedUserId,
                p_notification_type: 'ticket_mention'
              });

            if (prefError) {
              console.error('[ticketService] Error fetching user preferences:', {
                error: prefError,
                userId: mentionedUserId,
                errorDetails: {
                  code: prefError.code,
                  message: prefError.message,
                  details: prefError.details,
                  hint: prefError.hint
                }
              });
              continue;
            }

            console.log('[ticketService] User notification preferences:', {
              userId: mentionedUserId,
              preferences: preferences
            });

            // If no preferences found, create default preferences
            if (!preferences || preferences.length === 0) {
              console.log('[ticketService] Creating default notification preferences for user:', mentionedUserId);

              const tenantId = await getCurrentTenantId();
              const { data: newPreferences, error: insertError } = await supabase
                .from('notification_preferences')
                .insert({
                  user_id: mentionedUserId,
                  notification_type: 'mention',
                  tenant_id: tenantId,
                  in_app_enabled: true,
                  email_enabled: true,
                  sms_enabled: true,
                  slack_enabled: true
                })
                .select()
                .single();

              if (insertError) {
                console.error('[ticketService] Error creating default preferences:', {
                  error: insertError,
                  userId: mentionedUserId,
                  type: 'mention'
                });
                continue;
              }

              console.log('[ticketService] Created default preferences:', newPreferences);

              // Skip if both in-app and email notifications are disabled
              if (!newPreferences?.in_app_enabled && !newPreferences?.email_enabled) {
                console.log('[ticketService] Skipping notification - both in-app and email disabled for user:', mentionedUserId);
                continue;
              }
            } else {
              // Skip if both in-app and email notifications are disabled
              if (!preferences[0]?.in_app_enabled && !preferences[0]?.email_enabled) {
                console.log('[ticketService] Skipping notification - both in-app and email disabled for user:', mentionedUserId);
                continue;
              }
            }

            try {
              console.log('[ticketService] Creating mention notification for user:', mentionedUserId);
              
              const notification = await ticketNotificationService.createNotification({
                ticket_id: ticket.id,
                user_id: mentionedUserId,
                type: 'ticket_mention',
                message: `${userProfile?.name || 'Someone'} mentioned you in a comment on ticket: ${ticket.title}`
              });

              console.log('[ticketService] Successfully created notification:', {
                notificationId: notification.id,
                userId: mentionedUserId,
                ticketId: ticket.id
              });
              
            } catch (error) {
              console.error('[ticketService] Error creating mention notification:', {
                error,
                mentionedUserId,
                ticketId: ticket.id,
                commentId: comment.id,
                inAppEnabled: preferences?.[0]?.in_app_enabled,
                emailEnabled: preferences?.[0]?.email_enabled
              });
            }
          } else {
            console.log('[ticketService] Skipping self-notification for user:', mentionedUserId);
          }
        }
      } else {
        console.log('[ticketService] No mentions to process for comment:', comment.id);
      }

      // For non-internal comments, check preferences for ticket creator
      if (!input.is_internal && ticket.created_by !== user.id) {

        const { data: creatorPreferences, error: creatorPrefError } = await supabase
          .rpc('get_notification_preferences', {
            p_user_id: ticket.created_by,
            p_notification_type: 'comment'
          });

        if (creatorPrefError) {
          console.error('Error fetching creator preferences:', {
            error: creatorPrefError,
            creatorId: ticket.created_by,
            ticketId: ticket.id
          });
        } else {

          // If no preferences found, create default preferences
          if (!creatorPreferences || creatorPreferences.length === 0) {
            const creatorTenantId = await getCurrentTenantId();
            const { data: newPreferences, error: insertError } = await supabase
              .from('notification_preferences')
              .insert({
                user_id: ticket.created_by,
                notification_type: 'comment',
                tenant_id: creatorTenantId,
                in_app_enabled: true,
                email_enabled: true,
                sms_enabled: true,
                slack_enabled: true
              })
              .select()
              .single();

            if (insertError) {
              console.error('Error creating default preferences for creator:', {
                error: insertError,
                creatorId: ticket.created_by,
                type: 'comment'
              });
            } else {

              // Only create notification if either in-app or email is enabled
              if (newPreferences?.in_app_enabled || newPreferences?.email_enabled) {
                notificationPromises.push(
                  ticketNotificationService.createNotification({
                    ticket_id: ticket.id,
                    user_id: ticket.created_by,
                    type: 'comment',
                    message: `A new comment has been added to your ticket: ${ticket.title}`
                  }).catch(error => {
                    console.error('Error creating notification for ticket creator:', {
                      error,
                      creatorId: ticket.created_by,
                      ticketId: ticket.id,
                      commentId: comment.id,
                      inAppEnabled: newPreferences?.in_app_enabled,
                      emailEnabled: newPreferences?.email_enabled
                    });
                    return null;
                  })
                );
              }
            }
          } else {
            // Only create notification if either in-app or email is enabled
            if (creatorPreferences[0]?.in_app_enabled || creatorPreferences[0]?.email_enabled) {
              notificationPromises.push(
                ticketNotificationService.createNotification({
                  ticket_id: ticket.id,
                  user_id: ticket.created_by,
                  type: 'comment',
                  message: `A new comment has been added to your ticket: ${ticket.title}`
                }).catch(error => {
                  console.error('Error creating notification for ticket creator:', {
                    error,
                    creatorId: ticket.created_by,
                    ticketId: ticket.id,
                    commentId: comment.id,
                    inAppEnabled: creatorPreferences[0]?.in_app_enabled,
                    emailEnabled: creatorPreferences[0]?.email_enabled
                  });
                  return null;
                })
              );
            } else {
            }
          }
        }
      }

      // For assigned user, check preferences
      if (ticket.assigned_to && ticket.assigned_to !== user.id && ticket.assigned_to !== ticket.created_by) {

        // Check if user has in-app notifications enabled
        const { data: assignedUserPreferences, error: assignedUserPrefError } = await supabase
          .rpc('get_notification_preferences', {
            p_user_id: ticket.assigned_to,
            p_notification_type: 'comment'
          });

        if (assignedUserPrefError) {
          console.error('[ticketService] Error fetching notification preferences:', {
            error: assignedUserPrefError,
            userId: ticket.assigned_to,
            type: 'comment'
          });
        }

        // If no preferences found, create default preferences
        if (!assignedUserPreferences || assignedUserPreferences.length === 0) {
          const assignedTenantId = await getCurrentTenantId();
          const { data: newPreferences, error: insertError } = await supabase
            .from('notification_preferences')
            .insert({
              user_id: ticket.assigned_to,
              notification_type: 'comment',
              tenant_id: assignedTenantId,
              in_app_enabled: true,
              email_enabled: true,
              sms_enabled: true,
              slack_enabled: true
            })
            .select()
            .single();

          if (insertError) {
            console.error('[ticketService] Error creating default preferences for assigned user:', {
              error: insertError,
              assignedUserId: ticket.assigned_to,
              type: 'comment'
            });
          } else {

            // Only create notification if in-app notifications are enabled
            if (newPreferences?.in_app_enabled) {
              await ticketNotificationService.createNotification({
                type: 'comment',
                user_id: ticket.assigned_to,
                ticket_id: ticket.id,
                message: `A new comment has been added to ticket: ${ticket.title}`
              });
            } else {
            }
          }
        } else {
          // Only create notification if in-app notifications are enabled
          if (assignedUserPreferences[0]?.in_app_enabled) {
            await ticketNotificationService.createNotification({
              type: 'comment',
              user_id: ticket.assigned_to,
              ticket_id: ticket.id,
              message: `A new comment has been added to ticket: ${ticket.title}`
            });
          } else {
          }
        }
      }

      // Run all notifications and webhook in parallel

      await Promise.all([
        ...notificationPromises,
        webhookService.triggerWebhooks('ticket_comment', webhookData)
      ]);

      return comment;
    } catch (error) {
      console.error('Error creating comment:', error);
      throw error;
    }
  },

  // Add new method to mark comment as read
  async markCommentAsRead(commentId: string): Promise<void> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { error } = await supabase
        .from('ticket_comment_read_status')
        .upsert({
          comment_id: commentId,
          user_id: user.id,
          is_read: true,
          read_at: new Date().toISOString()
        }, {
          onConflict: 'comment_id,user_id'
        });

      if (error) throw error;
    } catch (error) {
      console.error('Error marking comment as read:', error);
      throw error;
    }
  },

  // Attachments
  async getTicketAttachments(ticketId: string): Promise<TicketAttachment[]> {    
    const { data, error } = await supabase
      .from('ticket_attachments')
      .select('*')
      .eq('ticket_id', ticketId)
      .order('uploaded_at', { ascending: false });

    if (error) {
      console.error('Error fetching attachments:', error);
      throw error;
    }
    
    return data || [];
  },

  async createAttachment(input: CreateAttachmentInput): Promise<TicketAttachment> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const fileExt = input.file.name.split('.').pop();
    const fileName = `${Math.random()}.${fileExt}`;
    const filePath = `tickets/${input.ticket_id}/${fileName}`;

    // Upload file to Supabase Storage
    const { error: uploadError } = await supabase.storage
      .from('ticket_attachments')
      .upload(filePath, input.file);

    if (uploadError) {
      console.error('Error uploading file:', uploadError);
      throw uploadError;
    }

    const tenantId = await getCurrentTenantId();
    
    const attachmentData = {
      ticket_id: input.ticket_id,
      file_name: input.file.name,
      file_path: filePath,
      file_type: input.file.type,
      file_size: input.file.size,
      uploaded_by: user.id,
      uploaded_at: new Date().toISOString(),
      tenant_id: tenantId
    };

    // Create attachment record
    const { data, error } = await supabase
      .from('ticket_attachments')
      .insert([attachmentData])
      .select()
      .single();

    if (error) {
      console.error('Error creating attachment record:', error);
      throw error;
    }

    return data;
  },

  // History
  async getTicketHistory(ticketId: string): Promise<TicketHistory[]> {
    const { data, error } = await supabase
      .from('ticket_history')
      .select('*')
      .eq('ticket_id', ticketId)
      .order('changed_at', { ascending: false });

    if (error) throw error;
    return data;
  },

  // Add new method to update comment
  async updateComment(commentId: string, content: string, mentionedUserIds: string[] = []): Promise<TicketComment> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Update the comment
      const { data: comment, error } = await supabase
        .from('ticket_comments')
        .update({
          content,
          mentioned_user_ids: mentionedUserIds,
          updated_at: new Date().toISOString()
        })
        .eq('id', commentId)
        .select(`
          *,
          user_profiles!created_by (
            id,
            name,
            email
          )
        `)
        .single();

      if (error) throw error;

      // Create read status entries for new mentions
      if (mentionedUserIds.length > 0) {
        const tenantId = await getCurrentTenantId();
        
        const readStatusEntries = mentionedUserIds.map(userId => ({
          comment_id: commentId,
          user_id: userId,
          is_read: false,
          tenant_id: tenantId
        }));

        const { error: readStatusError } = await supabase
          .from('ticket_comment_read_status')
          .insert(readStatusEntries);

        if (readStatusError) {
          console.error('Error creating read status entries:', readStatusError);
        }
      }

      return comment;
    } catch (error) {
      console.error('Error updating comment:', error);
      throw error;
    }
  },

  async deleteComment(commentId: string): Promise<void> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // First get the comment to check ownership
      const { data: comment, error: fetchError } = await supabase
        .from('ticket_comments')
        .select('created_by')
        .eq('id', commentId)
        .single();

      if (fetchError) throw fetchError;
      if (!comment) throw new Error('Comment not found');
      if (comment.created_by !== user.id) throw new Error('Not authorized to delete this comment');

      // Delete the comment
      const { error: deleteError } = await supabase
        .from('ticket_comments')
        .delete()
        .eq('id', commentId);

      if (deleteError) throw deleteError;
    } catch (error) {
      console.error('Error deleting comment:', error);
      throw error;
    }
  }
}; 